import React, { useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import add_product_icon from "../Components/Assets/Product_Cart.svg";
import list_product_icon from "../Components/Assets/Product_list_icon.svg";

// Pages accessible to 'admin' role (not super-admin only)
// eslint-disable-next-line no-unused-vars
const ADMIN_ALLOWED = [
  "/admin/dashboard", "/admin/addproduct", "/admin/listproduct",
  "/admin/orders", "/admin/payments", "/admin/coupons", "/admin/reviews",
];

const NAV_SECTIONS = [
  {
    title: "Overview",
    items: [
      { to: "/admin/dashboard", icon: "📊", label: "Dashboard" },
    ],
  },
  {
    title: "Products",
    items: [
      { to: "/admin/addproduct", icon: add_product_icon, isSvg: true, label: "Add Product" },
      { to: "/admin/listproduct", icon: list_product_icon, isSvg: true, label: "Product List" },
      { to: "/admin/categories", icon: "🗂️", label: "Categories" },
    ],
  },
  {
    title: "Commerce",
    items: [
      { to: "/admin/orders", icon: "🛒", label: "Orders" },
      { to: "/admin/payments", icon: "💳", label: "Payments" },
      { to: "/admin/coupons", icon: "🏷️", label: "Coupons" },
      { to: "/admin/reviews", icon: "⭐", label: "Reviews" },
    ],
  },  {
    title: "Content",
    items: [
      { to: "/admin/homepage", icon: "🏠", label: "Home Page" },
    ],
  },  {
    title: "Users",
    superAdminOnly: true,
    items: [
      { to: "/admin/users", icon: "👥", label: "Users" },
      { to: "/admin/management", icon: "🛡️", label: "Admin Management" },
    ],
  },
  {
    title: "System",
    superAdminOnly: true,
    items: [
      { to: "/admin/settings", icon: "⚙️", label: "Settings" },
    ],
  },
];

const ROLE_STYLES = {
  "super-admin": "bg-yellow-400/10 text-yellow-400 border border-yellow-400/30",
  "admin": "bg-[rgba(0,212,255,0.1)] text-[#00d4ff] border border-[rgba(0,212,255,0.3)]",
  "user": "bg-white/[0.06] text-[#a0aec0] border border-white/[0.1]",
};

const AdminPanel = () => {
  const role = localStorage.getItem('user-role') || 'user';
  const userName = localStorage.getItem('user-name') || 'Admin';
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('auth-token');
    localStorage.removeItem('user-role');
    localStorage.removeItem('user-name');
    navigate('/login');
  };

  // Filter sections by role
  const visibleSections = NAV_SECTIONS.filter(s => !s.superAdminOnly || role === 'super-admin');

  return (
    <div className="min-h-screen flex flex-col bg-[#060c18] overflow-x-hidden">

      {/* ── Top Navbar ── */}
      <header className="h-[60px] shrink-0 flex items-center justify-between px-5 bg-[#0a0f1e] border-b border-white/[0.07] z-50 relative max-[640px]:px-3">
        {/* Left */}
        <div className="flex items-center gap-3">
          {/* Hamburger - mobile only */}
          <button
            className="lg:hidden p-2 rounded-lg text-[#a0aec0] hover:text-white hover:bg-white/[0.05] transition-colors"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle menu"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              {sidebarOpen
                ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
                : <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>
              }
            </svg>
          </button>
          <Link to="/" className="flex items-center gap-2 text-[#00d4ff] font-black text-lg tracking-tight hover:opacity-80 transition-opacity max-[480px]:gap-1.5">
            ⚡ TechZone
          </Link>
          <span className="text-white/[0.15] text-lg hidden sm:inline">/</span>
          <span className="text-[#a0aec0] text-sm font-medium hidden sm:inline max-[480px]:hidden">Admin Panel</span>
        </div>

        {/* Right */}
        <div className="flex items-center gap-3">
          <Link
            to="/"
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-[#a0aec0] border border-white/[0.08] hover:text-[#00d4ff] hover:border-[rgba(0,212,255,0.3)] transition-all max-[900px]:hidden"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
            View Store
          </Link>

          {/* User info */}
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00d4ff] to-[#7c3aed] flex items-center justify-center text-[#060c18] font-black text-sm shrink-0">
              {(userName[0] || 'A').toUpperCase()}
            </div>
            <div className="hidden sm:flex flex-col">
              <span className="text-white text-xs font-semibold leading-none">{userName}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold mt-1 leading-none w-fit ${ROLE_STYLES[role] || ROLE_STYLES['user']}`}>
                {role}
              </span>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-400 border border-red-400/20 hover:bg-red-400/10 hover:border-red-400/40 transition-all cursor-pointer"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      {/* ── Body ── */}
      <div className="flex flex-1 min-h-0 relative">

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/60 z-[998] lg:hidden"
            style={{ top: '60px' }}
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside className={`
          fixed top-[60px] bottom-0 left-0 z-[999]
          w-[210px] shrink-0 bg-[#0a0f1e] border-r border-white/[0.07]
          flex flex-col py-4 overflow-y-auto
          transition-transform duration-300 ease-in-out
          max-[640px]:w-[85vw] max-[640px]:max-w-[290px]
          lg:relative lg:top-auto lg:bottom-auto lg:z-auto lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          {visibleSections.map((section) => (
            <div key={section.title} className="mb-3">
              <p className="text-[9px] uppercase tracking-widest text-[#2d3748] font-bold px-5 mb-1">
                {section.title}
              </p>
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-4 py-2.5 mx-2 rounded-xl text-sm font-medium transition-all mb-0.5 ${
                      isActive
                        ? "bg-[rgba(0,212,255,0.12)] text-[#00d4ff] border border-[rgba(0,212,255,0.22)]"
                        : "text-[#718096] hover:bg-white/[0.05] hover:text-[#e2e8f0]"
                    }`
                  }
                >
                  {item.isSvg ? (
                    <img src={item.icon} alt="" className="w-4 h-4 opacity-70" />
                  ) : (
                    <span className="text-base leading-none">{item.icon}</span>
                  )}
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </aside>

        {/* Main Content */}
        <main className="flex-1 bg-[#060c18] overflow-y-auto w-full min-w-0 max-[640px]:pb-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminPanel;
